import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2, Mail } from "lucide-react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import {
  extractStudentEmail,
  extractStudentJwtToken,
  extractStudentPreAuthToken,
  extractStudentUserRecord,
  isPendingStudentVerificationResponse,
  studentAuthApi,
} from "../../features/auth/authApi";
import { createStudentVerificationFlow } from "../../features/auth/studentOtpFlow";
import { finalizeStudentLogin } from "../../features/auth/useLogin";

const FALLBACK_ERROR_MESSAGE = "Unable to continue with OTP right now.";
const FALLBACK_SENT_MESSAGE = "Verification code sent to your email.";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getBackendMessage = (payload, fallbackMessage = "") =>
  String(payload?.message || payload?.detail || "").trim() || fallbackMessage;

const buildStudentOtpFlowSource = (mode) =>
  mode === "signup" ? "otp-signup" : "otp-login";

export default function StudentOtpRequestPage({ mode = "login" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isSignupMode = mode === "signup";
  const source = buildStudentOtpFlowSource(mode);
  const pageCopy = useMemo(
    () => ({
      title: isSignupMode ? "Continue with OTP" : "Login with OTP",
      description: isSignupMode
        ? "Enter your email and we'll send a one-time passcode to create your student account."
        : "Enter your email and we'll send a one-time passcode so you can log in without a password.",
      submitLabel: isSignupMode ? "Send signup OTP" : "Send login OTP",
      loadingLabel: isSignupMode ? "Sending signup OTP..." : "Sending login OTP...",
      alternateLabel: isSignupMode ? "Use other signup method" : "Use other login method",
      alternatePath: isSignupMode ? "/createaccount" : "/login",
      otpSwitchLabel: isSignupMode ? "Already have an account? Login with OTP" : "New here? Sign up with OTP",
      otpSwitchPath: isSignupMode ? "/login-otp" : "/createaccount-otp",
    }),
    [isSignupMode]
  );

  const otpRequestMutation = useMutation({
    mutationFn: (payload) =>
      isSignupMode
        ? studentAuthApi.signupWithOtp(payload)
        : studentAuthApi.loginWithOtp(payload),
    onSuccess: (response, variables) => {
      const responseData = response?.data || {};
      const studentJwt = extractStudentJwtToken(response);
      const normalizedEmail =
        extractStudentEmail(response) ||
        String(variables?.email || "").trim().toLowerCase();

      if (studentJwt) {
        const profileData = extractStudentUserRecord(response, {
          email: normalizedEmail,
          jwtoken: studentJwt,
        });

        finalizeStudentLogin({
          dispatch,
          navigate,
          profileData,
          shouldShowFirstSignupDemo: isSignupMode,
        });
        return;
      }

      const preAuthToken = extractStudentPreAuthToken(response);
      const requiresVerification =
        Boolean(preAuthToken) || isPendingStudentVerificationResponse(responseData);

      if (!requiresVerification) {
        setErrorMessage(getBackendMessage(responseData, FALLBACK_ERROR_MESSAGE));
        return;
      }

      const nextContext = createStudentVerificationFlow({
        email: normalizedEmail,
        preAuthToken,
        verificationType: responseData?.data?.verification_type || "otp",
        accountType: responseData?.data?.account_type || "student",
        canResendVerification: isSignupMode,
        source,
      });

      navigate("/verification", {
        replace: true,
        state: {
          email: nextContext.email,
          source,
          successMessage: getBackendMessage(responseData, FALLBACK_SENT_MESSAGE),
        },
      });
    },
    onError: (error, variables) => {
      const responseData = error?.response?.data || {};
      const normalizedEmail = String(variables?.email || "").trim().toLowerCase();
      const preAuthToken = extractStudentPreAuthToken(responseData);
      const requiresVerification =
        Boolean(preAuthToken) || isPendingStudentVerificationResponse(responseData);

      if (requiresVerification) {
        const nextContext = createStudentVerificationFlow({
          email: extractStudentEmail(responseData) || normalizedEmail,
          preAuthToken,
          verificationType: responseData?.data?.verification_type || "otp",
          accountType: responseData?.data?.account_type || "student",
          canResendVerification: isSignupMode,
          source,
        });

        navigate("/verification", {
          replace: true,
          state: {
            email: nextContext.email,
            source,
            successMessage: getBackendMessage(responseData, FALLBACK_SENT_MESSAGE),
          },
        });
        return;
      }

      setErrorMessage(getBackendMessage(responseData, FALLBACK_ERROR_MESSAGE));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");

    const normalizedEmail = String(email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      setErrorMessage("Email is required.");
      return;
    }

    if (!validateEmail(normalizedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    otpRequestMutation.mutate({ email: normalizedEmail });
  };

  return (
    <div
      className="login-page min-h-screen flex items-center justify-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        className="login-page-back absolute top-4 left-4 sm:top-5 sm:left-5 cursor-pointer"
        onClick={() => navigate(-1)}
        role="button"
        aria-label="Go back to the previous page"
      >
        <img
          src={GLOBAL_IMAGES.LoginBackButton}
          alt="Back"
          className="login-page-back-img w-10 h-10"
        />
      </div>
      <div className="login-page-card mx-3 w-full max-w-xl rounded-3xl bg-white/70 px-5 py-8 shadow-lg sm:px-8 sm:py-10 lg:px-12">
        <h2 className="login-page-title mb-4 text-center text-3xl font-bold text-gray-800">
          {pageCopy.title}
        </h2>
        <p className="login-page-description mb-8 text-center text-gray-600">
          {pageCopy.description}
        </p>

        <form
          className="login-page-form mx-auto flex w-full max-w-[400px] flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="login-page-field">
            <label
              className="login-page-label block text-gray-700 font-bold mb-2"
              htmlFor="student-otp-email"
            >
              Email
            </label>
            <div className="login-page-password-wrap relative">
              <input
                id="student-otp-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (errorMessage) {
                    setErrorMessage("");
                  }
                }}
                className="login-page-input w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Email Address"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                maxLength={254}
              />
              <Mail
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
          </div>

          {errorMessage ? (
            <div className="login-page-submit-error flex items-center gap-2 mb-2 px-4 py-2 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={otpRequestMutation.isPending}
            className="flex w-full items-center justify-center rounded-lg bg-blue-800 py-2 text-white transition duration-300 hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {otpRequestMutation.isPending ? (
              <>
                <Loader2 className="animate-spin mr-2" size={20} />
                {pageCopy.loadingLabel}
              </>
            ) : (
              pageCopy.submitLabel
            )}
          </button>

          <Link
            to={pageCopy.otpSwitchPath}
            className="pt-1 text-center text-blue-600 hover:underline"
          >
            {pageCopy.otpSwitchLabel}
          </Link>

          <Link
            to={pageCopy.alternatePath}
            className="pt-1 text-center text-blue-800 font-semibold hover:underline"
          >
            {pageCopy.alternateLabel}
          </Link>
        </form>
      </div>
    </div>
  );
}
